/* import __COLOCATED_TEMPLATE__ from './manage-connection.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import { task } from 'ember-concurrency-decorators';
import type ImportSource from 'embercom/models/import-service/import-source';
import { SourceType } from 'embercom/models/import-service/import-source';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

interface Args {
  isOpen: boolean;
  onClose: () => void;
  requireConversationSyncing?: boolean;
  redirectLocation?: string;
  onImportSourceCreated?: (importSource: ImportSource, selectedDomain: string) => void;
}

export default class StandaloneSalesforceManageConnection extends Component<Args> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare notificationsService: $TSFixMe;
  @service declare redirectService: any;
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare openCenteredWindowService: $TSFixMe;

  @tracked selectedDomain = this.salesforceConfig?.isSandbox ? 'sandbox' : 'production';
  @tracked importSource: ImportSource | undefined;

  get isStandaloneApp() {
    return this.appService.app.isStandaloneApp;
  }

  @action startConnectFlow(selectedDomain = 'production') {
    if (this.isStandaloneApp) {
      let uri = this.oauthUrls.connect;
      let params = `app_id_code=${this.appService.app.id}&sandbox=${selectedDomain === 'sandbox'}&is_conversation_syncing_enabled=${this.finStandaloneService.isConversationSyncingEnabled}`;
      if (this.args.redirectLocation) {
        params += `&connect_redirect_location=${this.args.redirectLocation}`;
      }
      this.redirectService.redirect(`${uri}?${params}`);
    } else {
      taskFor(this.startImportSourceFlow).perform();
    }
  }

  @action async reconnectApp() {
    if (this.finStandaloneService.salesforceConfig?.isSandbox) {
      this.startConnectFlow('sandbox');
    } else {
      this.startConnectFlow('production');
    }
  }

  @action async disconnectApp() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t(
        'standalone.salesforce.setup.manage-connection.sync-case-history-confirmation-message',
        {
          environmentLabel: this.salesforceConfig.environmentLabel,
        },
      ),
      confirmButtonText: this.intl.t(
        'standalone.salesforce.setup.manage-connection.sync-case-history-confirmation-button-label',
      ),
      primaryButtonType: 'primary-destructive',
    });

    try {
      if (isConfirmed) {
        await this.salesforceConfig.disconnectApp();
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'standalone.salesforce.setup.manage-connection.sync-case-history-confirmation-success-message',
          ),
        );
      }
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t(
          'standalone.salesforce.setup.manage-connection.sync-case-history-confirmation-error-message',
        ),
      });
    }
  }

  @action onDomainChange(domain: string) {
    this.selectedDomain = domain;
  }

  get oauthUrls() {
    let { protocol, host } = window.location;
    return {
      connect: `${protocol}//${host}/standalone/salesforce/connect`,
      connectCallback: `${protocol}//${host}/standalone/salesforce/connect_callback`,
    };
  }

  @task({ drop: true })
  *startImportSourceFlow(): TaskGenerator<void> {
    if (!this.importSource) {
      this.importSource = yield this.knowledgeHubService.createImportSource({
        sourceType: SourceType.Salesforce,
      });
    }

    if (!this.importSource) {
      return;
    }
    let url = this.knowledgeHubService.getOAuthURL(
      this.importSource.id,
      this.selectedDomain === 'sandbox',
    );
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    let window = this.openCenteredWindowService.setup(url, 800, 1280, 'Import');
    if (window) {
      window.focus();
      yield this.waitForWindowClose(window);
    }

    yield this.importSource.reload();
    if (!this.importSource.hasCredentials) {
      yield this.importSource.remove({ notify: false });
    } else {
      this.args.onImportSourceCreated
        ? this.args.onImportSourceCreated(this.importSource, this.selectedDomain)
        : null;
    }
  }

  private waitForWindowClose(window: any): Promise<void> {
    return new Promise((resolve) => {
      let checkInterval = setInterval(() => {
        if (window.closed) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 200);
    });
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get isAuthFailed() {
    return this.salesforceConfig?.isAuthFailed;
  }

  get isConnected() {
    return this.salesforceConfig?.isConnected;
  }

  get authFailedBannerText() {
    return this.salesforceConfig?.isSandbox
      ? this.intl.t('standalone.salesforce.setup.manage-connection.auth-failed-banner-sandbox-text')
      : this.intl.t(
          'standalone.salesforce.setup.manage-connection.auth-failed-banner-production-text',
        );
  }

  get connectedBannerText() {
    return this.salesforceConfig?.isSandbox
      ? this.intl.t('standalone.salesforce.setup.manage-connection.connected-banner-sandbox-text')
      : this.intl.t(
          'standalone.salesforce.setup.manage-connection.connected-banner-production-text',
        );
  }

  get isSaveDisabled() {
    return (
      this.args.requireConversationSyncing &&
      !this.finStandaloneService.isConversationSyncingEnabled
    );
  }

  get tooltipMessageStepsToConnect() {
    return this.intl.t(
      'standalone.salesforce.setup.connect.tooltip-message-when-not-connected-and-syncing-required',
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::ManageConnection': typeof StandaloneSalesforceManageConnection;
    'standalone/salesforce/setup/manage-connection': typeof StandaloneSalesforceManageConnection;
  }
}
